import React from 'react';
import { ProducerApiError, fetchAnimals, fetchDealDetail, uploadInvoice } from './api';
import type { AnimalRow, DealDetail, HealthRecord, MovementStep, Performance, ProducerDocument } from './api';
import { AlertIcon, BackIcon, CheckIcon, FileIcon, PillIcon, SearchIcon, SyringeIcon, UploadIcon } from './icons';
import { Empty, ErrorBox, LoadingBlock, Pill, fmtDate, fmtNum, stageTone, useLoad } from './ui';

export type DealTab = 'overview' | 'animals' | 'health' | 'documents';
const TABS: { key: DealTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'animals', label: 'Animals' },
  { key: 'health', label: 'Health' },
  { key: 'documents', label: 'Documents' },
];

/* ── Overview ───────────────────────────────────────── */

const Timeline: React.FC<{ steps: MovementStep[] }> = ({ steps }) => {
  if (!steps.length) {
    return <Empty title="No movements yet">Each time your animals move or are received, it will appear here.</Empty>;
  }
  return (
    <ol className="pf-line">
      {steps.map((s, i) => (
        <li className="pf-step" key={s.id}>
          <span className={`pf-dot ${s.kind === 'farm' ? 'is-farm' : ''} ${s.code === 'ABAT_SLA' && i === steps.length - 1 ? 'is-end' : ''}`}>
            <CheckIcon size={16} />
          </span>
          <div>
            <div className="pf-step-t">{s.label}</div>
            <div className="pf-step-s">
              {fmtNum(s.animals)} {s.animals === 1 ? 'animal' : 'animals'}{s.place ? ` · ${s.place}` : ''}
            </div>
            {s.date && <div className="pf-step-d">{fmtDate(s.date)}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
};

const PerfLedger: React.FC<{ p: Performance }> = ({ p }) => {
  const row = (label: string, hint: string, v: number | null | undefined, unit: string, places: number) => (
    <li key={label}>
      <span className="k"><b>{label}</b><span className="hint">{hint}</span></span>
      {v === null || v === undefined
        ? <span className="v is-empty">Not yet</span>
        : <span className="v">{fmtNum(v, places)}<small>{unit}</small></span>}
    </li>
  );
  return (
    <ul className="pf-ledger">
      {row('Daily weight gain', 'Kg gained per animal per day', p.avg_daily_gain_kg, 'kg/day', 2)}
      {row('Time in the feedlot', 'Average days on feed', p.avg_days_in_feedlot, 'days', 0)}
      {row('Feed conversion', p.feed_conversion_is_estimate ? 'Estimated. Kg of feed per kg gained' : 'Kg of feed per kg gained. Lower is better', p.feed_conversion, ':1', 2)}
      {row('Dressing percentage', 'Carcass weight as a share of live weight', p.dressing_percentage, '%', 1)}
    </ul>
  );
};

const Overview: React.FC<{ d: DealDetail }> = ({ d }) => {
  const c = d.counts;
  return (
    <div className="pf-screen">
      <section aria-labelledby="h-herd">
        <h2 className="pf-h2" id="h-herd" style={{ marginBottom: 10 }}>The herd</h2>
        <dl className="pf-ledger">
          <div><dt>Animals in this deal</dt><dd>{fmtNum(c.total)}</dd></div>
          {c.alive > 0 && <div><dt>Still alive</dt><dd>{fmtNum(c.alive)}</dd></div>}
          {c.slaughtered > 0 && <div><dt>Slaughtered</dt><dd>{fmtNum(c.slaughtered)}</dd></div>}
          {c.deceased > 0 && <div><dt>Died</dt><dd>{fmtNum(c.deceased)}</dd></div>}
        </dl>
      </section>
      <section className="pf-section" aria-labelledby="h-move">
        <h2 className="pf-h2" id="h-move" style={{ marginBottom: 14 }}>Where they have been</h2>
        <div className="pf-panel"><Timeline steps={d.movement} /></div>
      </section>
      <section className="pf-section" aria-labelledby="h-perf">
        <h2 className="pf-h2" id="h-perf" style={{ marginBottom: 10 }}>Performance</h2>
        <PerfLedger p={d.performance} />
        {!d.performance.sample_size && d.performance.dressing_percentage === null && (
          <p className="pf-small" style={{ marginTop: 10 }}>
            These fill in once your animals have been weighed more than once and slaughtered.
          </p>
        )}
      </section>
    </div>
  );
};

/* ── Animals ────────────────────────────────────────── */

const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'ACTIVE', label: 'Alive' },
  { key: 'SLAUGHTERED', label: 'Slaughtered' },
  { key: 'DECEASED', label: 'Died' },
];

const Animals: React.FC<{ id: string }> = ({ id }) => {
  const [q, setQ] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [debounced, setDebounced] = React.useState('');
  const [rows, setRows] = React.useState<AnimalRow[]>([]);
  const [count, setCount] = React.useState(0);
  const [state, setState] = React.useState<'loading' | 'ok' | 'error'>('loading');
  const [more, setMore] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  React.useEffect(() => {
    let live = true;
    setState('loading');
    fetchAnimals(id, { q: debounced, status }).then(
      r => { if (live) { setRows(r.results); setCount(r.count); setState('ok'); } },
      (e: any) => { if (live) { setErr(e?.message || 'Could not load animals.'); setState('error'); } },
    );
    return () => { live = false; };
  }, [id, debounced, status, tick]);

  const loadMore = async () => {
    setMore(true);
    try {
      const r = await fetchAnimals(id, { q: debounced, status, offset: rows.length });
      setRows(prev => [...prev, ...r.results]);
    } catch (e: any) {
      setErr(e?.message || 'Could not load more.');
      setState('error');
    } finally { setMore(false); }
  };

  return (
    <div className="pf-screen pf-stack">
      <label className="pf-field">
        <span className="pf-sr">Search animals by tag number</span>
        <SearchIcon size={20} />
        <input type="search" inputMode="search" placeholder="Search by tag number" value={q} onChange={e => setQ(e.target.value)} />
      </label>
      <div className="pf-chips" role="group" aria-label="Filter animals">
        {STATUS_FILTERS.map(f => (
          <button key={f.key} type="button" className="pf-chip" aria-pressed={status === f.key} onClick={() => setStatus(f.key)}>
            {f.label}
          </button>
        ))}
      </div>
      {state === 'loading' ? <LoadingBlock rows={4} /> : state === 'error' ? (
        <ErrorBox message={err} onRetry={() => setTick(t => t + 1)} />
      ) : rows.length === 0 ? (
        <Empty title="No animals found">{debounced || status ? 'Try a different search or filter.' : 'Animals appear here once they are loaded for this deal.'}</Empty>
      ) : (
        <>
          <p className="pf-small" aria-live="polite">{fmtNum(count)} {count === 1 ? 'animal' : 'animals'}</p>
          <ul className="pf-rows">
            {rows.map(a => (
              <li key={a.id}>
                <div style={{ minWidth: 0 }}>
                  <div className="pf-animal-id">{a.eid || a.vid || 'No tag'}</div>
                  <div className="pf-row-s">{[a.sex, a.breed].filter(Boolean).join(' · ')}</div>
                  <div style={{ marginTop: 6 }}>
                    <Pill tone={a.status_code === 'ACTIVE' ? 'is-good' : a.status_code === 'DECEASED' ? 'is-bad' : ''}>{a.status}</Pill>
                    {a.location && <span className="pf-small" style={{ marginLeft: 8 }}>{a.location}</span>}
                  </div>
                </div>
                {a.last_weight_kg !== null && (
                  <div className="pf-weight">
                    <b>{fmtNum(a.last_weight_kg, 0)}</b> <span className="pf-small">kg</span>
                    <div className="pf-small">{fmtDate(a.last_weighed)}</div>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {rows.length < count && (
            <button type="button" className="pf-btn is-ghost is-block" onClick={loadMore} disabled={more}>
              {more ? 'Loading…' : `Show more (${fmtNum(count - rows.length)} left)`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

/* ── Health ─────────────────────────────────────────── */

const withdrawal = (iso: string | null) => {
  if (!iso) return null;
  const end = new Date(`${iso}T23:59:59`);
  return end.getTime() > Date.now()
    ? { tone: 'is-warn', text: `Withdrawal until ${fmtDate(iso)}` }
    : { tone: 'is-good', text: 'Withdrawal period over' };
};

const Health: React.FC<{ items: HealthRecord[] }> = ({ items }) => {
  if (!items.length) {
    return <div className="pf-screen"><Empty title="No treatments recorded">Vaccinations and medicines given to this deal's animals will be listed here.</Empty></div>;
  }
  return (
    <div className="pf-screen">
      <ul className="pf-rows">
        {items.map(h => {
          const w = withdrawal(h.withdrawal_ends);
          return (
            <li key={h.id}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                <span style={{ color: 'var(--pf-sky)' }}>{h.kind === 'vaccination' ? <SyringeIcon /> : <PillIcon />}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="pf-row-t">{h.name}</div>
                  <div className="pf-row-s">
                    {[h.type, h.animals ? `${fmtNum(h.animals)} animals` : '', fmtDate(h.date)].filter(Boolean).join(' · ')}
                  </div>
                  {w && <div style={{ marginTop: 6 }}><Pill tone={w.tone}>{w.text}</Pill></div>}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/* ── Documents + invoice upload ─────────────────────── */

const todayIso = () => new Date().toISOString().slice(0, 10);

const InvoiceForm: React.FC<{ dealId: string; onFiled: (d: ProducerDocument) => void }> = ({ dealId, onFiled }) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [ref, setRef] = React.useState('');
  const [date, setDate] = React.useState(todayIso());
  const [amount, setAmount] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [fields, setFields] = React.useState<Record<string, string>>({});
  const [error, setError] = React.useState('');
  const input = React.useRef<HTMLInputElement>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const local: Record<string, string> = {};
    if (!file) local.file = 'Choose the invoice file to upload.';
    if (!ref.trim()) local.reference_number = 'Enter the invoice number.';
    if (!date) local.date_of_document = 'Enter the date on the invoice.';
    const cleaned = amount.replace(/\s/g, '').replace(',', '.');
    if (cleaned && !/^\d+(\.\d{1,2})?$/.test(cleaned)) local.amount = 'Enter the invoice total as a number, for example 125000.50.';
    else if (cleaned && Number(cleaned) <= 0) local.amount = 'Enter an invoice total above zero.';
    setFields(local); setError('');
    if (Object.keys(local).length || !file) return;
    setBusy(true);
    try {
      const doc = await uploadInvoice(dealId, { file, reference_number: ref.trim(), date_of_document: date, amount: cleaned });
      setFile(null); setRef(''); setDate(todayIso()); setAmount('');
      onFiled(doc);
    } catch (err) {
      if (err instanceof ProducerApiError) { setFields(err.fields); setError(err.fields.detail || ''); }
      else setError('Could not upload. Check your connection and try again.');
    } finally { setBusy(false); }
  };

  return (
    <form className="pf-panel pf-stack" onSubmit={submit} noValidate aria-labelledby="h-inv">
      <div>
        <h3 className="pf-h2" id="h-inv">Send us your invoice</h3>
        <p className="pf-small">For animals you are selling to AVCS. A PDF, or a clear photo, up to 10 MB.</p>
      </div>
      <div>
        <input ref={input} id="inv-file" className="pf-sr" type="file" accept="application/pdf,image/jpeg,image/png"
          onChange={e => { setFile(e.target.files?.[0] || null); setFields(f => ({ ...f, file: '' })); }} />
        <button type="button" className={`pf-pick ${file ? 'has-file' : ''}`} onClick={() => input.current?.click()} aria-describedby={fields.file ? 'e-file' : undefined}>
          {file ? <CheckIcon /> : <UploadIcon />}
          <span>{file ? file.name : 'Choose file or take a photo'}<small>{file ? `${(file.size / 1024 / 1024).toFixed(1)} MB — tap to change` : 'PDF, JPG or PNG'}</small></span>
        </button>
        {fields.file && <p className="pf-err" id="e-file">{fields.file}</p>}
      </div>
      <div>
        <label className="pf-label" htmlFor="inv-ref">Invoice number</label>
        <input id="inv-ref" className="pf-input" value={ref} maxLength={100} autoComplete="off"
          aria-invalid={!!fields.reference_number} onChange={e => { setRef(e.target.value); setFields(f => ({ ...f, reference_number: '' })); }} />
        {fields.reference_number && <p className="pf-err">{fields.reference_number}</p>}
      </div>
      <div>
        <label className="pf-label" htmlFor="inv-date">Invoice date</label>
        <input id="inv-date" className="pf-input" type="date" value={date} max={todayIso()}
          aria-invalid={!!fields.date_of_document} onChange={e => { setDate(e.target.value); setFields(f => ({ ...f, date_of_document: '' })); }} />
        {fields.date_of_document && <p className="pf-err">{fields.date_of_document}</p>}
      </div>
      <div>
        <label className="pf-label" htmlFor="inv-amount">Invoice total <span className="pf-small" style={{ fontWeight: 500 }}>(optional)</span></label>
        <div className="pf-money">
          <span aria-hidden="true">R</span>
          <input id="inv-amount" className="pf-input" inputMode="decimal" autoComplete="off" placeholder="0.00"
            value={amount} aria-invalid={!!fields.amount} aria-describedby="inv-amount-help"
            onChange={e => { setAmount(e.target.value); setFields(f => ({ ...f, amount: '' })); }} />
        </div>
        <p className="pf-small" id="inv-amount-help" style={{ marginTop: 6 }}>The total on your invoice, excluding VAT. AVCS checks it against the file.</p>
        {fields.amount && <p className="pf-err">{fields.amount}</p>}
      </div>
      {error && <div className="pf-banner is-bad" role="alert"><AlertIcon size={20} /><span>{error}</span></div>}
      <button type="submit" className="pf-btn is-block" disabled={busy}>{busy ? 'Sending…' : 'Send invoice'}</button>
    </form>
  );
};

const Documents: React.FC<{ d: DealDetail; onFiled: () => void }> = ({ d, onFiled }) => {
  const [justFiled, setJustFiled] = React.useState<string | null>(null);
  return (
    <div className="pf-screen pf-stack">
      {justFiled && (
        <div className="pf-banner is-good" role="status"><CheckIcon size={20} />
          <span>Invoice {justFiled} sent. AVCS has it now.</span></div>
      )}
      {d.documents.length === 0 ? (
        <Empty title="No documents yet">Documents AVCS shares with you, and invoices you send, will be listed here.</Empty>
      ) : (
        <ul className="pf-rows">
          {d.documents.map(doc => (
            <li key={doc.id}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                <span style={{ color: 'var(--pf-sky)' }}><FileIcon /></span>
                <div style={{ minWidth: 0 }}>
                  <div className="pf-row-t">{doc.name}{doc.mine && <> <Pill tone="is-quiet">You sent this</Pill></>}</div>
                  <div className="pf-row-s">{[doc.reference, fmtDate(doc.date)].filter(Boolean).join(' · ')}</div>
                </div>
              </div>
              {doc.file && (
                <a className="pf-link" href={doc.file} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center' }}>Open</a>
              )}
            </li>
          ))}
        </ul>
      )}
      {d.can_upload_invoice && (
        <InvoiceForm dealId={d.id} onFiled={doc => { setJustFiled(doc.reference); onFiled(); }} />
      )}
    </div>
  );
};

/* ── Shell for one deal ─────────────────────────────── */

export const DealView: React.FC<{
  id: string; tab: DealTab; onTab: (t: DealTab) => void; onBack: () => void;
}> = ({ id, tab, onTab, onBack }) => {
  const { data, error, status, loading, retry } = useLoad<DealDetail>(() => fetchDealDetail(id), id);
  const [silent, setSilent] = React.useState<DealDetail | null>(null);
  const d = silent || data;

  // After an upload, re-read quietly so the new file appears without a loading flash.
  const refresh = React.useCallback(() => {
    fetchDealDetail(id).then(setSilent, () => {});
  }, [id]);
  React.useEffect(() => { setSilent(null); }, [id]);

  return (
    <div className="pf-screen">
      <header className="pf-top" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 6px)' }}>
        <div className="pf-wrap">
          <button type="button" className="pf-back" onClick={onBack}><BackIcon size={22} />All deals</button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <h1 className="pf-h1">{d?.deal_no || ' '}</h1>
              <p className="pf-sub" style={{ marginTop: 4 }}>
                {d ? [d.species, d.programme, d.started ? `since ${fmtDate(d.started)}` : ''].filter(Boolean).join(' · ') : ' '}
              </p>
            </div>
            {d && <Pill tone={stageTone(d.stage)}>{d.stage}</Pill>}
          </div>
        </div>
      </header>
      <div className="pf-tabs">
        <div className="pf-tabs-in" role="tablist" aria-label="Deal sections">
          {TABS.map(t => (
            <button key={t.key} role="tab" type="button" className="pf-tab" id={`tab-${t.key}`}
              aria-selected={tab === t.key} aria-controls="pf-panel" onClick={() => onTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <main className="pf-wrap" style={{ paddingTop: 20 }} id="pf-panel" role="tabpanel" aria-labelledby={`tab-${tab}`}>
        {loading && !d ? <LoadingBlock rows={4} /> : error && !d ? (
          status === 404 ? (
            <div className="pf-stack" role="alert">
              <Empty title="Deal not found">{error}</Empty>
              <button type="button" className="pf-btn is-block" onClick={onBack}>Back to your deals</button>
            </div>
          ) : <ErrorBox message={error} onRetry={retry} />
        ) : d ? (
          tab === 'overview' ? <Overview d={d} />
            : tab === 'animals' ? <Animals id={d.id} />
            : tab === 'health' ? <Health items={d.health} />
            : <Documents d={d} onFiled={refresh} />
        ) : null}
      </main>
    </div>
  );
};
